/**
 * Responsibility: describe the wooded lake biome — gentle ground with both trees and small lakes on it.
 *
 * The third of three lake biomes, and the one that needed real composition rather than just a config block.
 *
 * ## Why the trees belong here and not on the glacial lakeland
 *
 * The bumpy ground of `lakeland` implies rock, so trees on it would look like they were growing out of scree. Soft rounded
 * ground is where woodland belongs. The three divide as bumpy-and-bare, gentle-and-bare, gentle-and-wooded, so each pair
 * differs in exactly one way — which is what makes them tell apart from the air.
 *
 * Plain forest stays as it is. This is not a replacement for it.
 *
 * ## The composition, and why it is needed
 *
 * Cross-biome clearance in `tileContent.js` deliberately never lets a biome see its own reservation, on the grounds that a
 * biome guarantees its own structures do not intersect. This biome has two kinds that can — **trees would grow in its own
 * lakes** — so the guarantee has to be made here: the lake clearance is folded into what the tree placer treats as
 * reserved ground. Exactly what `rockFormations.js` does for spires and arches.
 *
 * Water wins over trees, because a tree standing in a lake is the bug and a lake beside a wood is the point.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createLakeTerrain } from '../lakeBasins.js';
import { createWaterSurface, createWaterClearance } from '../waterSurface.js';
import { createTreeStands } from '../treeStands.js';
import { WOODED_LAKES, PALETTE, FOREST_TREE_PALETTE } from '../../config.js';

export const woodedLakes = {
  id: 'woodedLakes',
  label: 'Wooded lakes',

  palette: PALETTE.woodedLakes,

  /**
   * Trees get their own gradient, as in the forest.
   *
   * The water does *not* read from this — its structure kind uses a fixed water gradient. That is what lets a single biome
   * hold both green trees and cyan water, which it could not do while a biome had only one structure palette to give.
   */
  structurePalette: FOREST_TREE_PALETTE,

  suitabilityBias: WOODED_LAKES.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    return createLakeTerrain(seed, 'woodedLakes', WOODED_LAKES).elevation;
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: WOODED_LAKES.BASE_HEIGHT + WOODED_LAKES.BASE_RELIEF + WOODED_LAKES.GROUND_RELIEF,

  /**
   * Build the structure placement function: lakes, and trees that keep out of them.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    const terrain = createLakeTerrain(seed, 'woodedLakes', WOODED_LAKES);

    const surface = createWaterSurface({
      ownDepthAt: terrain.ownDepth,

      // Flat by construction: the base field only, with none of this biome's ground detail in it.
      waterLevelAt: terrain.waterLevel,
      couldHoldWater: terrain.insideBasin,
      grid: WOODED_LAKES.WATER_GRID,
      maxDepth: WOODED_LAKES.DEPTH - WOODED_LAKES.WATER_DROP,
    });

    const shore = createWaterClearance({
      ownDepthAt: terrain.ownDepth,
      nearWater: terrain.nearBasin,
    });

    const trees = createTreeStands(seed, 'woodedLakes');

    return function collectStructures(region, context) {
      /*
        The shore test is evaluated from the terrain, not from the water this tile happened to emit. Filtering against a
        per-tile list would leave trees standing in the half of every lake that the neighbouring tile owns — the same trap
        the rock formations had with arches.
      */
      const withLakes = {
        ...context,
        isReserved: (x, z, radius) =>
          shore(x, z, radius, context) || (context.isReserved?.(x, z, radius) ?? false),
      };

      return [...surface(region, context), ...trees(region, withLakes)];
    };
  },

  /**
   * Publish the lakes so other biomes keep out of them too.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    const terrain = createLakeTerrain(seed, 'woodedLakes', WOODED_LAKES);

    return createWaterClearance({
      ownDepthAt: terrain.ownDepth,
      nearWater: terrain.nearBasin,
    });
  },
};
